import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { getIntro, introStore } from '@/lib/intro'

const MIN_MS = 1200
const MAX_MS = 2000

/**
 * "Registering points 000": counts with the real loading of the scene, never faster than 1.2 s
 * and never slower than 2.0 s, then reads "Registered". Shown only in the full intro.
 */
export const IntroCounter: FC = (): ReactNode => {
    const labelRef = useRef<HTMLSpanElement>(null)
    const valueRef = useRef<HTMLSpanElement>(null)

    useEffect(() => {
        let raf = 0
        let shown = 0
        let last = performance.now()
        let done = false

        const finishAt = (): number => {
            const { origin, readyAt } = getIntro()

            if (readyAt === null) return origin + MAX_MS

            return Math.min(origin + MAX_MS, Math.max(origin + MIN_MS, readyAt + 150))
        }

        const write = (value: number): void => {
            if (valueRef.current) valueRef.current.textContent = String(Math.round(value)).padStart(3, '0')
        }

        const frame = (now: number): void => {
            const intro = getIntro()

            if (intro.mode !== 'full' || intro.stage === 'idle' || intro.stage === 'skip') return

            const { origin } = intro
            const end = finishAt()
            const dt = Math.min(0.1, (now - last) / 1000)
            const target = Math.min(100, Math.max(0, ((now - origin) / (end - origin)) * 100))

            last = now
            shown = Math.max(shown, shown + (target - shown) * Math.min(1, dt * 12))

            if (now >= end) {
                done = true
                write(100)

                if (labelRef.current) labelRef.current.textContent = 'Registered'
                if (valueRef.current) valueRef.current.style.visibility = 'hidden'

                return
            }

            write(Math.min(99, shown))
            raf = requestAnimationFrame(frame)
        }

        const start = (): void => {
            cancelAnimationFrame(raf)

            if (!done) raf = requestAnimationFrame(frame)
        }

        start()

        const unsubscribe = introStore.subscribe(() => {
            if (introStore.getStage() === 'pre') start()
        })

        return () => {
            cancelAnimationFrame(raf)
            unsubscribe()
        }
    }, [])

    return (
        <p className='intro-counter hud pointer-events-none absolute bottom-4 left-gutter z-[2] whitespace-nowrap text-mute desk:bottom-7' aria-hidden='true'>
            <span ref={labelRef}>
                Registering points
            </span>

            <span ref={valueRef} className='ml-3 text-ink'>
                000
            </span>
        </p>
    )
}
